import { useCallback, useState } from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import { trpc, trpcUtils } from "@editor/utils/trpc";

/**
 * Hook for setting up a domain that has a verified CNAME record.
 *
 * This hook handles the internal domain setup process by:
 * - Adding the domain to our Cloudflare zone
 * - Adding the domain to our database
 * - Reconciling the KV store with the database
 */
const useSetupValidatedDomain = () => {
  const { projectId } = useCurrentProjectContext();
  const { workspaceId } = useCurrentWorkspaceContext();

  const { mutateAsync: createCustomDomainInDatabase } =
    trpc.v2CustomDomain.createCustomDomainInDatabase.useMutation();

  const { mutateAsync: createCustomDomainInCloudflare } =
    trpc.v2CustomDomain.createCustomDomainInCloudflare.useMutation();

  const [
    isValidatedDomainSetupInProgress,
    setIsValidatedDomainSetupInProgress,
  ] = useState(false);

  const setupValidatedDomain = useCallback(
    async (domain: string) => {
      if (!projectId || !workspaceId) {
        throw new Error("Project or workspace not found");
      }

      setIsValidatedDomainSetupInProgress(true);

      const { cloudflareCustomHostnameId } =
        await createCustomDomainInCloudflare({
          projectId,
          customDomain: domain,
          workspaceId,
        });

      await createCustomDomainInDatabase({
        projectId,
        customDomain: domain,
        workspaceId,
        cloudflareCustomHostnameId,
      });

      await trpcUtils.v2CustomDomain.getByProjectId.invalidate({
        projectId,
      });

      setIsValidatedDomainSetupInProgress(false);
    },
    [
      projectId,
      workspaceId,
      createCustomDomainInCloudflare,
      createCustomDomainInDatabase,
    ],
  );

  return { setupValidatedDomain, isValidatedDomainSetupInProgress };
};

export default useSetupValidatedDomain;
